import { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";

// Sort ids the parts listings endpoint understands - "created" falls through to the
// default branch of ApplySorting (ordered by PartData.Created), while the listing date
// sort uses the date of the offer each row actually displays.
export const CREATED_DATE_SORT_ID = "created";
export const LISTED_DATE_SORT_ID = "offers.listingStarted";

interface ITableDateSortControlProps {
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  disabled?: boolean;
}

const fieldSegments = [
  { value: CREATED_DATE_SORT_ID, label: "Created date" },
  { value: LISTED_DATE_SORT_ID, label: "Listed date" },
];

const directionSegments = [
  { value: true, label: "Newest first" },
  { value: false, label: "Oldest first" },
];

function Segmented<T extends string | boolean>({
  segments,
  value,
  onChange,
  disabled,
  ariaLabel,
}: {
  segments: { value: T; label: string }[];
  value: T | undefined;
  onChange: (next: T) => void;
  disabled: boolean;
  ariaLabel: string;
}) {
  const { t } = useTranslation("common");

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={classNames("inline-flex rounded-md border border-gray-300 bg-white p-0.5", disabled ? "opacity-50" : "")}
    >
      {segments.map((segment) => {
        const active = value === segment.value;
        return (
          <button
            key={String(segment.value)}
            type="button"
            disabled={disabled}
            onClick={() => onChange(segment.value)}
            className={classNames(
              "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap",
              active ? "bg-primary-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100",
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            )}
            aria-pressed={active}
          >
            {t(segment.label)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Switches the table between the created date and the listing date. Sorting on any other
 * column (by clicking its header) leaves both date segments unselected rather than showing
 * a selection the table isn't actually using.
 */
export default function TableDateSortControl({
  sorting,
  onSortingChange,
  disabled = false,
}: ITableDateSortControlProps) {
  const { t } = useTranslation("common");
  const current = sorting[0];
  const isDateSort = current?.id === CREATED_DATE_SORT_ID || current?.id === LISTED_DATE_SORT_ID;

  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-black">{t("Sort By")}</span>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Segmented
          segments={fieldSegments}
          value={isDateSort ? current.id : undefined}
          onChange={(id) => onSortingChange([{ id, desc: current?.desc ?? true }])}
          disabled={disabled}
          ariaLabel={t("Sort By")}
        />
        <Segmented
          segments={directionSegments}
          value={isDateSort ? current.desc : undefined}
          onChange={(desc) => onSortingChange([{ id: isDateSort ? current.id : CREATED_DATE_SORT_ID, desc }])}
          disabled={disabled}
          ariaLabel={t("Sort Direction")}
        />
      </div>
    </div>
  );
}
